import { useEffect, useState } from "react";
import type { UserProfile } from "../../../shared/types";
import { PageHeader } from "../components/common";
import { BORDER_RADIUS, COLORS, SPACING, TYPOGRAPHY } from "../constants/theme";
import { useAuth } from "../hooks/useAuth";
import { EditUserModal } from "../components/modals";
import { getManagerStaff } from "../services/user.service";

export function ManagerTeam() {
  const { currentUser } = useAuth();
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingStaff, setEditingStaff] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (currentUser?.uid) loadStaff();
  }, [currentUser?.uid]);

  const loadStaff = async () => {
    if (!currentUser?.uid) return;
    setLoading(true);
    try {
      const members = await getManagerStaff(currentUser.uid);
      setStaff(members);
    } catch (error) {
      console.error("Error loading team:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStaff = staff.filter((member) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      member.firstName?.toLowerCase().includes(searchLower) ||
      member.lastName?.toLowerCase().includes(searchLower) ||
      member.email.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div style={{ padding: SPACING["2xl"], textAlign: "center", color: COLORS.text.secondary }}>
        Loading team...
      </div>
    );
  }

  return (
    <div style={{ padding: SPACING["2xl"] }}>
      <PageHeader title="Team" subtitle="Staff members assigned to you" />

      <div style={{ marginBottom: SPACING.xl }}>
        <input
          type="text"
          placeholder="Search team members..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: `${SPACING.sm} ${SPACING.lg}`,
            fontSize: TYPOGRAPHY.fontSize.sm,
            border: `1px solid ${COLORS.border.medium}`,
            borderRadius: BORDER_RADIUS.md,
            outline: "none",
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = COLORS.primary.main; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = COLORS.border.medium; }}
        />
      </div>

      <div style={{ backgroundColor: COLORS.background.primary, borderRadius: BORDER_RADIUS.xl, border: `1px solid ${COLORS.border.light}`, overflow: "auto" }}>
        {filteredStaff.length === 0 ? (
          <div style={{ padding: SPACING["2xl"], textAlign: "center" }}>
            <p style={{ fontSize: TYPOGRAPHY.fontSize.base, color: COLORS.text.secondary }}>
              {searchTerm ? "No staff members found matching your search." : "No staff members assigned yet."}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="responsive-table">
              <thead style={{ backgroundColor: COLORS.background.secondary }}>
                <tr>
                  {["Name", "Email", "Phone", ""].map((h, i) => (
                    <th key={i} style={{ padding: SPACING.lg, textAlign: i === 3 ? "center" : "left", fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.semibold, color: COLORS.text.secondary }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((member) => (
                  <tr key={member.uid} style={{ borderTop: `1px solid ${COLORS.border.light}` }}>
                    <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.primary }}>
                      {member.firstName} {member.lastName}
                    </td>
                    <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary }}>
                      {member.email}
                    </td>
                    <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary }}>
                      {member.phoneNumber ? `${member.countryCode || ''} ${member.phoneNumber}`.trim() : "N/A"}
                    </td>
                    <td style={{ padding: SPACING.lg, textAlign: "center" }}>
                      <button onClick={() => setEditingStaff(member)} style={{ width: "64px", padding: "5px 0", fontSize: "13px", fontWeight: 500, color: "#374151", backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "6px", cursor: "pointer" }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile card list */}
            <div className="team-card-list">
              {filteredStaff.map((member) => (
                <div key={member.uid} className="team-card">
                  <div className="team-card-name">{member.firstName} {member.lastName}</div>
                  <div className="team-card-field">{member.email}</div>
                  <div className="team-card-field">{member.phoneNumber ? `${member.countryCode || ''} ${member.phoneNumber}`.trim() : "N/A"}</div>
                  <div className="team-card-actions">
                    <button onClick={() => setEditingStaff(member)} style={{ padding: "5px 16px", fontSize: "13px", fontWeight: 500, color: "#374151", backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "6px", cursor: "pointer" }}>Edit</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {editingStaff && currentUser?.companyId && (
        <EditUserModal
          isOpen={!!editingStaff}
          onClose={() => setEditingStaff(null)}
          user={editingStaff}
          companyId={currentUser.companyId}
          canEditEmail={false}
          onSuccess={() => { setEditingStaff(null); loadStaff(); }}
        />
      )}
    </div>
  );
}
